import { useEffect, useMemo, useRef, useState } from "react";
import type { ConversationResponse } from "../api/contract.gen.ts";
import { useServices } from "../app/services.ts";
import { avatarUrl } from "../data/avatars.ts";
import { partner, plain, preview, shortTime, sortConversations, title, VOXIE_ID } from "../data/display.ts";
import { useStore } from "../data/store.ts";
import { Avatar } from "../ui/Avatar.tsx";
import { Icon } from "../ui/Icon.tsx";
import styles from "./ChatList.module.css";

type Filter = "all" | "unread" | "groups";

/** The chat list with search (⌘K) and filters, docs/web-designs screen 2. */
export function ChatList({ selected, onOpen }: { selected: string | null; onOpen: (conversationId: string, messageId?: string) => void }) {
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  const search = useRef<HTMLInputElement>(null);
  const { conversations, me, online, version } = useStore((s) => ({ conversations: [...s.conversations.values()], me: s.me, online: s.online, version: s.getVersion() }));
  const { store } = useServices();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        search.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sortConversations(conversations)
      .filter((c) => !c.archived)
      .filter((c) => (filter === "unread" ? c.unread_count > 0 : filter === "groups" ? c.kind === "group" : true))
      .filter((c) => !q || title(c, me).toLowerCase().includes(q) || c.members.some((m) => (m.user.display_name ?? "").toLowerCase().includes(q)));
  }, [conversations, filter, query, me, version]); // eslint-disable-line react-hooks/exhaustive-deps

  // Messages that match, from what this browser has loaded (the app searches its database the same way).
  const hits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const out: { conversation: ConversationResponse; id: string; text: string; at: string }[] = [];
    for (const c of conversations) {
      for (const m of store.messagesOf(c.id)) {
        if (m.response.deleted) continue;
        const text = m.content.text ?? m.response.transcript?.text ?? m.content.fileName ?? "";
        if (text.toLowerCase().includes(q)) out.push({ conversation: c, id: m.response.id, text: plain(text), at: m.response.created_at });
      }
    }
    return out.sort((a, b) => b.at.localeCompare(a.at)).slice(0, 50);
  }, [query, conversations, version]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className={styles.list} aria-label="Chats">
      <div className={styles.head}>
        <h1>Chats</h1>
      </div>
      <label className={styles.search}>
        <Icon name="search" />
        <input ref={search} placeholder="Suchen" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Escape" && setQuery("")} />
        <kbd>⌘ K</kbd>
      </label>
      <div className={styles.chips} role="tablist">
        {(
          [
            ["all", "Alle"],
            ["unread", "Ungelesen"],
            ["groups", "Gruppen"],
          ] as const
        ).map(([key, text]) => (
          <button key={key} role="tab" aria-selected={filter === key} className={filter === key ? `${styles.chip} ${styles.on}` : styles.chip} onClick={() => setFilter(key)}>
            {text}
          </button>
        ))}
      </div>
      <div className={styles.rows}>
        {list.map((c) => (
          <Row key={c.id} conversation={c} me={me} selected={c.id === selected} online={online} onOpen={() => onOpen(c.id)} />
        ))}
        {list.length === 0 && <p className={styles.empty}>{conversations.length === 0 ? "Chats werden geladen …" : "Keine Chats gefunden."}</p>}
        {hits.length > 0 && (
          <>
            <div className={styles.section}>NACHRICHTEN</div>
            {hits.map((h) => (
              <button key={h.id} className={styles.hit} onClick={() => onOpen(h.conversation.id, h.id)}>
                <b>{title(h.conversation, me)}</b>
                <span>{h.text}</span>
              </button>
            ))}
          </>
        )}
      </div>
    </section>
  );
}

function Row({ conversation: c, me, selected, online, onOpen }: { conversation: ConversationResponse; me: string | null; selected: boolean; online: Set<string>; onOpen: () => void }) {
  const { session, store } = useServices();
  const p = partner(c, me);
  const [src, setSrc] = useState<string>();
  useEffect(() => {
    if (p && c.kind !== "agent") void avatarUrl(session, p.id, p.avatar).then(setSrc);
  }, [p?.id, p?.avatar]); // eslint-disable-line react-hooks/exhaustive-deps
  const last = c.last_message ? store.message(c.id, c.last_message.id) : undefined;
  const activity = (store.activity.get(c.id) ?? []).find((a) => a.until > Date.now() && a.user !== me);
  const name = title(c, me);
  return (
    <button className={selected ? `${styles.row} ${styles.selected}` : styles.row} onClick={onOpen} aria-current={selected}>
      <Avatar id={p?.id ?? c.id} name={c.kind === "group" ? name : name} src={src} online={Boolean(p && p.id !== me && online.has(p.id))} voxie={c.kind === "agent" || p?.id === VOXIE_ID} />
      <span className={styles.text}>
        <b>{name}</b>
        <span className={activity ? styles.live : undefined}>{activity ? (activity.what === "recording" ? "nimmt auf …" : "schreibt …") : preview(c, me, last)}</span>
      </span>
      <span className={styles.meta}>
        {c.last_message && shortTime(c.last_message.created_at)}
        {c.unread_count > 0 && <span className={styles.count}>{c.unread_count}</span>}
      </span>
    </button>
  );
}
